import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../foundation/altzero_base/base/components/ui/card";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import {
  ArrowLeft,
  Search,
  FileText,
  Calendar,
  Building,
  Loader2,
  CheckCircle,
  Copy,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/dialog";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import { useUser } from "../../../foundation/altzero_base/base/contextapi/UserContext";
import ScopingAILayout from "../components/ScopingAILayout";

// Types
interface ExistingProposal {
  id: string;
  title: string;
  client_name: string;
  created_at: string;
  status: string;
  description?: string;
  sections_count?: number;
}

const CreateFromExisting: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useUser();

  // State
  const [proposals, setProposals] = useState<ExistingProposal[]>([]);
  const [filteredProposals, setFilteredProposals] = useState<ExistingProposal[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Modal and pagination state
  const [showAllProposalsModal, setShowAllProposalsModal] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PROPOSALS_PER_PAGE = 12;

  // Helper function to get visible proposals (first 6)
  const getVisibleProposals = () => {
    const visible = filteredProposals.slice(0, 6);
    console.log("🎯 CreateFromExisting - Showing proposals:", {
      total: filteredProposals.length,
      visible: visible.length,
      showSeeMore: filteredProposals.length > 6
    });
    return visible;
  };

  // Load existing proposals
  useEffect(() => {
    loadProposals();
  }, []);

  // Filter proposals based on search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredProposals(proposals);
    } else {
      const filtered = proposals.filter(proposal =>
        proposal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proposal.client_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProposals(filtered);
    }
  }, [searchTerm, proposals]);

  const loadProposals = async () => {
    try {
      setIsLoading(true);
      console.log("📄 Loading existing documents from scopingai_documents table (including shared)...");

      if (!user?.id) {
        console.warn("❌ No user ID available for loading documents");
        setProposals([]);
        setFilteredProposals([]);
        return;
      }

      // Import supabase client and document sharing service
      const { supabase } = await import("../../../foundation/altzero_base/base/utils/supabaseClient");
      const { getSharedDocuments } = await import("../services/documentSharingService");

      // 🚨 FIXED: Fetch both owned and shared documents
      console.log("📄 Fetching owned documents...");
      const { data: ownedData, error: ownedError } = await supabase
        .from("scopingai_documents")
        .select(
          `
          id,
          title,
          created_at,
          updated_at,
          status,
          content,
          metadata,
          client_id,
          user_id,
          crm_companies (
            name
          )
        `
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (ownedError) {
        console.error("Error loading owned documents:", ownedError);
        throw ownedError;
      }

      // 🚨 NEW: Fetch shared documents
      console.log("📄 Fetching shared documents...");
      let sharedData: any[] = [];
      try {
        const sharedDocuments = await getSharedDocuments(user.id);
        sharedData = sharedDocuments.map((doc: any) => ({
          id: doc.document_id,
          title: doc.title,
          created_at: doc.shared_at,
          updated_at: doc.shared_at,
          status: doc.status || "shared",
          content: null, // Shared documents might not have full content
          metadata: { shared: true, permission: doc.permission_level },
          client_id: null,
          user_id: doc.shared_by,
          crm_companies: doc.crm_companies || null
        }));
        console.log(`📄 Found ${sharedData.length} shared documents`);
      } catch (sharedError) {
        console.warn("⚠️ Could not load shared documents:", sharedError);
        // Continue without shared documents
      }

      // Combine owned and shared documents
      const allData = [...(ownedData || []), ...sharedData];

      // Transform the combined data to match ExistingProposal interface
      const transformedProposals: ExistingProposal[] = allData.map((doc: any) => {
        const isShared = doc.metadata?.shared === true;
        const clientName = doc.crm_companies?.name || "No Client";

        return {
          id: doc.id,
          title: doc.title || "Untitled Document",
          client_name: isShared ? `${clientName} (Shared)` : clientName,
          created_at: doc.created_at,
          status: doc.status || "completed",
          description: doc.content?.sections?.[0]?.content?.substring(0, 150) + "..." ||
                      (isShared ? "Shared document - content available after selection" : "No description available"),
          sections_count: doc.content?.sections?.length || 0
        };
      });

      console.log("📄 Loaded and transformed proposals:", {
        total: transformedProposals.length,
        owned: ownedData?.length || 0,
        shared: sharedData.length,
        proposals: transformedProposals
      });
      setProposals(transformedProposals);
      setFilteredProposals(transformedProposals);
    } catch (error) {
      console.error("❌ Failed to load existing proposals:", error);
      toast({
        title: "Error",
        description: "Failed to load existing proposals",
        variant: "destructive"
      });
      setProposals([]);
      setFilteredProposals([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProposalSelect = (proposal: ExistingProposal) => {
    // Create template info object that matches the expected format
    const templateInfo = {
      id: `existing_${proposal.id}`,
      title: proposal.title,
      type: "existing_proposal",
      source: "Existing Proposal",
      original_client: proposal.client_name,
      sections_count: proposal.sections_count,
      description: proposal.description,
      created_at: proposal.created_at,
      // Add the original document ID for potential content retrieval
      original_document_id: proposal.id
    };

    console.log("🎯 Selected existing proposal as template:", templateInfo);

    // Navigate to the normal DocumentsNew flow with the selected proposal as template
    const templateParam = encodeURIComponent(JSON.stringify(templateInfo));
    navigate(`/tender/documents/new?template=${templateParam}`, {
      state: { fromExistingFlow: true }
    });
  };

  const handleBack = () => {
    navigate('/tender/new-proposal');
  };

  const renderContent = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Select Existing Proposal</h2>
          <p className="text-muted-foreground">Choose a proposal to use as a template for your new project</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search proposals by title or client name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Proposals Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading proposals...</span>
        </div>
      ) : filteredProposals.length === 0 ? (
        <div className="text-center py-8">
          {searchTerm ? (
            // Search results empty state
            <div>
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No proposals found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search terms
              </p>
            </div>
          ) : (
            // No proposals at all - Creative empty state
            <div className="max-w-2xl mx-auto">
              {/* Compact Animated Icon */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full animate-pulse"></div>
                </div>
                <div className="relative flex items-center justify-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>

              {/* Main Message */}
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
                Ready to Create Your First Proposal?
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                You don't have any existing proposals yet, but that's perfectly fine!
                Let's get you started with creating your first professional proposal.
              </p>

              {/* Action Cards */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {/* AI Generation Card */}
                <div
                  onClick={() => navigate('/tender/new-proposal')}
                  className="group p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 hover:border-blue-300 cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-105"
                >
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-500 rounded-lg mb-3 group-hover:bg-blue-600 transition-colors">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-semibold text-blue-900 mb-2">AI-Powered Proposal</h3>
                  <p className="text-blue-700 text-sm leading-relaxed">
                    Let our AI create a comprehensive proposal for you. Just provide project details and we'll handle the rest.
                  </p>
                  <div className="mt-3 text-blue-600 text-sm font-medium group-hover:text-blue-700">
                    Start with AI →
                  </div>
                </div>

                {/* Quick Proposal Card */}
                <div
                  onClick={() => navigate('/tender/quick-proposal')}
                  className="group p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200 hover:border-green-300 cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-105"
                >
                  <div className="flex items-center justify-center w-10 h-10 bg-green-500 rounded-lg mb-3 group-hover:bg-green-600 transition-colors">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </div>
                  <h3 className="text-base font-semibold text-green-900 mb-2">Quick Proposal</h3>
                  <p className="text-green-700 text-sm leading-relaxed">
                    Create a proposal quickly using our streamlined process. Perfect for simple projects and fast turnarounds.
                  </p>
                  <div className="mt-3 text-green-600 text-sm font-medium group-hover:text-green-700">
                    Quick Start →
                  </div>
                </div>
              </div>

              {/* Additional Help */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 mb-1">New to proposal creation?</h4>
                    <p className="text-sm text-gray-600">
                      Don't worry! Our AI will guide you through each step and help you create professional proposals that win clients.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getVisibleProposals().map((proposal) => (
            <Card
              key={proposal.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleProposalSelect(proposal)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{proposal.title}</CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <Building className="h-4 w-4" />
                      {proposal.client_name}
                    </CardDescription>
                  </div>
                  <Badge variant="secondary">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {proposal.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {proposal.description}
                </p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {new Date(proposal.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    {proposal.sections_count} sections
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>

          {/* See More Proposals Button */}
          {filteredProposals.length > 6 && (
            <div className="flex justify-center mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setModalSearchQuery("");
                  setCurrentPage(1);
                  setShowAllProposalsModal(true);
                }}
                className="gap-2 bg-primary/10 border-primary/30 text-primary font-semibold text-lg px-8 py-4 shadow-md hover:bg-primary/20 transition-all"
                style={{ minWidth: 220 }}
              >
                <Search className="w-5 h-5" />
                See More Proposals
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <ScopingAILayout>
      <div className="container mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Header with back button */}
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleBack} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Create from Existing Proposal</h1>
              <p className="text-muted-foreground">
                Select a proposal to use as a template, then follow the normal proposal creation process
              </p>
            </div>
          </div>

          {/* Content */}
          {renderContent()}

          {/* See More Proposals Modal */}
          <Dialog
            open={showAllProposalsModal}
            onOpenChange={setShowAllProposalsModal}
          >
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-primary" />
                  All Existing Proposals ({proposals.length})
                </DialogTitle>
                <DialogDescription>
                  Browse and select from all your existing proposal documents
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4">
                {/* Search Bar */}
                <div className="relative mb-6">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search proposals by title or client..."
                    value={modalSearchQuery}
                    onChange={(e) => setModalSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Proposals Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(() => {
                    const filteredModalProposals = proposals.filter((proposal) =>
                      modalSearchQuery === ""
                        ? true
                        : proposal.title
                            .toLowerCase()
                            .includes(modalSearchQuery.toLowerCase()) ||
                          proposal.client_name
                            .toLowerCase()
                            .includes(modalSearchQuery.toLowerCase())
                    );

                    const startIndex = (currentPage - 1) * PROPOSALS_PER_PAGE;
                    const endIndex = startIndex + PROPOSALS_PER_PAGE;
                    const paginatedProposals = filteredModalProposals.slice(startIndex, endIndex);

                    return paginatedProposals.map((proposal) => (
                      <Card
                        key={proposal.id}
                        className="hover:shadow-md transition-shadow cursor-pointer hover:border-primary/50"
                        onClick={() => {
                          handleProposalSelect(proposal);
                          setShowAllProposalsModal(false);
                        }}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-sm font-medium line-clamp-2">
                                {proposal.title}
                              </CardTitle>
                              <CardDescription className="flex items-center gap-1 mt-1 text-xs">
                                <Building className="h-3 w-3" />
                                {proposal.client_name}
                              </CardDescription>
                            </div>
                            <Badge variant="secondary" className="text-xs">
                              <CheckCircle className="h-2 w-2 mr-1" />
                              {proposal.status}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                            {proposal.description}
                          </p>
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-2 w-2" />
                              {new Date(proposal.created_at).toLocaleDateString()}
                            </div>
                            <div className="flex items-center gap-1">
                              <FileText className="h-2 w-2" />
                              {proposal.sections_count} sections
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ));
                  })()}
                </div>

                {/* Pagination */}
                {(() => {
                  const filteredModalProposals = proposals.filter((proposal) =>
                    modalSearchQuery === ""
                      ? true
                      : proposal.title
                          .toLowerCase()
                          .includes(modalSearchQuery.toLowerCase()) ||
                        proposal.client_name
                          .toLowerCase()
                          .includes(modalSearchQuery.toLowerCase())
                  );

                  const totalPages = Math.ceil(filteredModalProposals.length / PROPOSALS_PER_PAGE);

                  return totalPages > 1 ? (
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : null;
                })()}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </ScopingAILayout>
  );
};

export default CreateFromExisting;
